import qs from "qs";
import {FilterDef, HTTPGenericResponse} from "../../constants/FinanceModels";
import {getFilterValue} from "../../utils/FilterUtils";
import {useApi} from "../Api";
import {
    ID_FILTER_AMOUNT_FROM,
    ID_FILTER_AMOUNT_TO,
    ID_FILTER_CORRELATION_ID,
    ID_FILTER_CORRELATION_NAME,
    ID_FILTER_CREATION_DATE_FROM,
    ID_FILTER_CREATION_DATE_TO,
    ID_FILTER_CUSTOMER_COMPANY_NAME,
    ID_FILTER_CUSTOMER_FIRST_NAME,
    ID_FILTER_CUSTOMER_ID,
    ID_FILTER_CUSTOMER_LAST_NAME,
    ID_FILTER_DEALERS,
    ID_FILTER_DUE_DATE_FROM,
    ID_FILTER_DUE_DATE_TO,
    ID_FILTER_INVOICE_NUMBER,
    ID_FILTER_INVOICEDATEFROM,
    ID_FILTER_INVOICEDATETO,
    ID_FILTER_INVOICETYPE,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_PARENT_SAP_INVOICE_NUMBER,
    ID_FILTER_PAYMENT_METHOD,
    ID_FILTER_RA_CODE,
    ID_FILTER_SAP_INVOICE_NUMBER,
    ID_FILTER_SEARCH,
    ID_FILTER_STATUS,
    ID_FILTER_TERMS_OF_PAYMENT
} from "../../pages/invoices/constants/InvoicesFilters";
import dayjs from "dayjs";
import {ModifiedItem} from "../../pages/invoices/sections/InvoiceItems";
import {useContext} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";

export const useInvoiceRepository = () => {
    const {GET, POST, DELETE, PUT} = useApi();

    const PATH = "/finance/invoice"

    const {globalData: {user}} = useContext(GlobalContext);
    const allLegalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all").map((x: any) => x.name);

    function getInvoices(offset: number, limit: number, sort: string | null, order: string | null, filterDefs: FilterDef[] = [], customerUrl: string | null): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let customerFirstName = getFilterValue(ID_FILTER_CUSTOMER_FIRST_NAME, filterDefs);
        let customerLastName = getFilterValue(ID_FILTER_CUSTOMER_LAST_NAME, filterDefs);
        let customerId = customerUrl ? customerUrl : getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs);
        let customerCompanyName = getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs);
        let types = getFilterValue(ID_FILTER_INVOICETYPE, filterDefs);
        let statuses = getFilterValue(ID_FILTER_STATUS, filterDefs);
        let dateFrom: Date = getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs);
        let dateTo: Date = getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs);
        let invoiceNumber = getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
        let sapInvoiceNumber = getFilterValue(ID_FILTER_SAP_INVOICE_NUMBER, filterDefs);
        let parentSapInvoiceNumber = getFilterValue(ID_FILTER_PARENT_SAP_INVOICE_NUMBER, filterDefs);
        let raCode = getFilterValue(ID_FILTER_RA_CODE, filterDefs);
        let correlationName = getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs);
        let correlationId = getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs);

        let dueDateFrom: Date = getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs);
        let dueDateTo: Date = getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs);
        let creationDateFrom: Date = getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs);
        let creationDateTo: Date = getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs);
        let paymentMethod = getFilterValue(ID_FILTER_PAYMENT_METHOD, filterDefs);
        let termsOfPayment = getFilterValue(ID_FILTER_TERMS_OF_PAYMENT, filterDefs);
        let amountFrom: number = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
        let amountTo: number = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            offset: offset,
            limit: limit,
            sort: sort,
            order: order,
            search: search,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName,
            customerId: customerId,
            customerCompanyName: customerCompanyName,
            types: types,
            statuses: statuses,
            invoiceNumber: invoiceNumber,
            sapInvoiceNumber: sapInvoiceNumber,
            parentSapInvoiceNumber: parentSapInvoiceNumber,
            raCode: raCode,
            from: dateFrom ? dayjs(dateFrom).toISOString() : null,
            to: dateTo ? dayjs(dateTo).toISOString() : null,
            accountType: "RECEIVABLE",
            correlationName: correlationName,
            correlationId: correlationId,
            dueDateFrom: dueDateFrom,
            dueDateTo: dueDateTo,
            creationDateFrom: creationDateFrom,
            creationDateTo: creationDateTo,
            paymentMethod: paymentMethod,
            termsOfPayment: termsOfPayment,
            amountFrom: amountFrom ? amountFrom * 100 : amountFrom,
            amountTo: amountTo ? amountTo * 100 : amountTo,
            legalEntities,
            dealers
        }

        return GET(`${PATH}/all`, {
            params: params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
    }

    function getInvoicesByParentId(parentId: string, type: string[] | undefined, status: string[] | undefined, offset: number, limit: number, sort: string | null, order: string | null, legalEntity: string, dealer: string): Promise<HTTPGenericResponse> {
        let params = {
            offset: offset,
            limit: limit,
            sort: sort,
            order: order,
            parentId: parentId,
            types: type,
            statuses: status,
            legalEntities: [legalEntity],
            dealers: [dealer]
        }

        return GET(`${PATH}/all`, {
            params: params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
    }

    function getInvoiceById(id: string, loading: boolean = true): Promise<HTTPGenericResponse> {
        return GET(`${PATH}/${id}`, undefined, loading);
    }

    function getSummary(filterDefs: FilterDef[] = []): Promise<HTTPGenericResponse> {
        let search = getFilterValue(ID_FILTER_SEARCH, filterDefs);
        let customerFirstName = getFilterValue(ID_FILTER_CUSTOMER_FIRST_NAME, filterDefs);
        let customerLastName = getFilterValue(ID_FILTER_CUSTOMER_LAST_NAME, filterDefs);
        let customerId = getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs);
        let customerCompanyName = getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs);
        let types = getFilterValue(ID_FILTER_INVOICETYPE, filterDefs);
        let statuses = getFilterValue(ID_FILTER_STATUS, filterDefs);
        let dateFrom: Date = getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs);
        let dateTo: Date = getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs);
        let invoiceNumber = getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
        let sapInvoiceNumber = getFilterValue(ID_FILTER_SAP_INVOICE_NUMBER, filterDefs);
        let parentSapInvoiceNumber = getFilterValue(ID_FILTER_PARENT_SAP_INVOICE_NUMBER, filterDefs);
        let raCode = getFilterValue(ID_FILTER_RA_CODE, filterDefs);
        let correlationName = getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs);
        let correlationId = getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs);

        let dueDateFrom: Date = getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs);
        let dueDateTo: Date = getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs);
        let creationDateFrom: Date = getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs);
        let creationDateTo: Date = getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs);
        let paymentMethod = getFilterValue(ID_FILTER_PAYMENT_METHOD, filterDefs);
        let termsOfPayment = getFilterValue(ID_FILTER_TERMS_OF_PAYMENT, filterDefs);
        let amountFrom: number = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
        let amountTo: number = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
        let legalEntities: string[] = getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
        let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

        let params = {
            search: search,
            customerFirstName: customerFirstName,
            customerLastName: customerLastName,
            customerId: customerId,
            customerCompanyName: customerCompanyName,
            invoiceNumber: invoiceNumber,
            sapInvoiceNumber: sapInvoiceNumber,
            parentSapInvoiceNumber: parentSapInvoiceNumber,
            raCode: raCode,
            types: types,
            statuses: statuses,
            from: dateFrom ? dayjs(dateFrom).toISOString() : null,
            to: dateTo ? dayjs(dateTo).toISOString() : null,
            accountType: "RECEIVABLE",
            correlationName: correlationName,
            correlationId: correlationId,
            dueDateFrom: dueDateFrom,
            dueDateTo: dueDateTo,
            creationDateFrom: creationDateFrom,
            creationDateTo: creationDateTo,
            paymentMethod: paymentMethod,
            termsOfPayment: termsOfPayment,
            amountFrom: amountFrom ? amountFrom * 100 : amountFrom,
            amountTo: amountTo ? amountTo * 100 : amountTo,
            legalEntities,
            dealers
        }

        return GET(`${PATH}/all-types-summary`, {
            params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        });
    }


    function postCreditNote(id: string, items: ModifiedItem[], legalEntity: string, dealer: string): Promise<HTTPGenericResponse> {
        let params = {
            invoiceItems: items,
            legalEntity,
            dealer
        }
        return POST(`${PATH}/${id}/credit-notes`, params, {}, true);
    }

    function postDebitNote(id: string, items: ModifiedItem[], legalEntity: string, dealer: string): Promise<HTTPGenericResponse> {
        let params = {
            invoiceItems: items,
            legalEntity,
            dealer
        }
        return POST(`${PATH}/${id}/debit-notes`, params, {}, true);
    }


    function deleteInvoice(id: string): Promise<HTTPGenericResponse> {
        return DELETE(`${PATH}/${id}`, {}, true);
    }

    function downloadPDF(id: string) {
        return GET(`${PATH}/pdf/${id}`);
    }

    function downloadXML(id: string) {
        return GET(`${PATH}/xml/${id}`);
    }

    function reissueInvoice(id: string): Promise<HTTPGenericResponse> {
        return PUT(`${PATH}/reissue/${id}`, {});
    }

    function reissueInvoiceNewCustomerId(id: string, customerId: string): Promise<HTTPGenericResponse> {
        return PUT(`${PATH}/reissue/${id}/${customerId}`, {});
    }

    function reprocessInvoice(id: string): Promise<HTTPGenericResponse> {
        return PUT(`${PATH}/reprocess/${id}`, {});
    }

    function enableAssistedDebitNote(id: string) {
        return GET(`${PATH}/${id}/debit-note-assisted/visibility`);
    }

    function assistedDebitNoteValues(id: any, newCompanyDate: string) {
        var companyDate = dayjs(newCompanyDate).toISOString()
        return GET(`${PATH}/${id}/debit-note-assisted/values?newCompanyDate=${companyDate}`);

    }

    function rentingBoundsInfo(periodId: string) {
        return GET(`/pricing/renting-bounds-info?anyPeriodId=${periodId}`);
    }

    function updateStatus(id: string, status: string): Promise<HTTPGenericResponse> {
        return PUT(`${PATH}/${id}/status`, {newStatus: status});
    }

    function uploadLeasePlanFile(dealer: any, legalEntity: any, file: File): Promise<HTTPGenericResponse> {
        const formData = new FormData();
        formData.append('dealer', dealer);
        formData.append('legalEntity', legalEntity);
        formData.append('file', file);

        return POST(`${PATH}/leaseplan/csv`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }, true);
    }

    function exportLeasePlanFile(dealers: any, legalEntities: any, dateFrom: any, dateTo: any): Promise<string> {
        let params = {
            dealers: dealers,
            legalEntities: legalEntities,
            dateFrom: dateFrom ? dayjs(dateFrom).toISOString() : null,
            dateTo: dateTo ? dayjs(dateTo).toISOString() : null,
        }
        return GET(`${PATH}/download-lease-plan-file`, {
            params: params,
            paramsSerializer: (params) => qs.stringify(params, {arrayFormat: 'repeat', skipNulls: true})
        }, true);
    }

    function getCustomer(customerId: string) {
        return GET(`finance/cb/${customerId}`, {}, true);
    }

    function getVariationNotesSummary(id: string) {
       return GET(`${PATH}/${id}/variation-notes-summary`, {}, true);
    }

    return {
        getInvoices,
        getInvoicesByParentId,
        getSummary,
        getInvoiceById,
        postCreditNote,
        postDebitNote,
        downloadPDF,
        deleteInvoice,
        reissueInvoice,
        reissueInvoiceNewCustomerId,
        reprocessInvoice,
        downloadXML,
        enableAssistedDebitNote,
        assistedDebitNoteValues,
        rentingBoundsInfo,
        updateStatus,
        uploadLeasePlanFile,
        exportLeasePlanFile,
        getCustomer,
        getVariationNotesSummary
    };
}